import type { ReactNode } from 'react';
import { DataTable } from './DataTable';
import { ClinicalMarkdownBody } from './clinicalMarkdownComponents';
import { S2Card } from './S2Card';
import { stripSectionScaffolding } from './stripSectionScaffolding';
import { SystemDisclosure } from './SystemDisclosure';
import { type CodeRef } from './CodeReferencesPanel';
import { ResponseSection } from './ResponseSection';
import { DailyMedSafetyPanel } from './DailyMedSafetyPanel';
import type { DailyMedPanelData, NestedListItem, StructuredSection } from './types';

const isTable = (s: StructuredSection) => s.type === 'table' && s.columns && s.rows?.length;
const isList = (s: StructuredSection) => s.type === 'list' && Array.isArray(s.items) && s.items.length;
const isNestedList = (s: StructuredSection) =>
  s.type === 'nested_list' && Array.isArray(s.items) && s.items.length;
const isParagraph = (s: StructuredSection) => s.type === 'paragraph' && String(s.content ?? '').trim();
// #81: pipeline/process status text (e.g. a branch timeout note) - always
// rendered distinct from clinical content, never counted toward the
// panel's item count.
const isStatus = (s: StructuredSection) => s.type === 'status' && String(s.content ?? '').trim();
// #89: system self-correction / inference-grouping caveats - small print,
// rendered distinct from clinical content, never counted toward the
// panel's item count (same treatment as `status`, different content class).
const isDisclosure = (s: StructuredSection) => s.type === 'disclosure' && String(s.content ?? '').trim();
// Care plans (and any other accordion payload) arrive as `cards`. This filter
// is a whitelist, so an unlisted type is not merely styled differently - it is
// dropped, and a card whose ONLY section was `cards` rendered as nothing at
// all. Found while wiring the branch above; a resource silently losing its
// whole content is not something to leave in place because it predates the
// change that exposed it.
const isCards = (s: StructuredSection) => s.type === 'cards' && Array.isArray(s.items) && s.items.length;
// #34/#35: the DailyMed FDA-label panel. The backend sends all five severity
// sections even when empty (the empty-state line is part of the spec), so
// presence of the sections array is the render condition, not item counts.
const isDailyMedPanel = (s: StructuredSection) =>
  s.type === 'dailymed_panel' &&
  !!s.data &&
  Array.isArray((s.data as DailyMedPanelData).sections) &&
  (s.data as DailyMedPanelData).sections.length > 0;

/** The whitelist above, applied. Anything not listed is dropped, not restyled. */
export function renderableSectionsOf(sections: StructuredSection[]): StructuredSection[] {
  return sections.filter(
    (s) =>
      isTable(s) || isList(s) || isNestedList(s) || isParagraph(s) || isStatus(s) || isDisclosure(s) || isCards(s) || isDailyMedPanel(s),
  );
}

// #89 (rule 16 "Count-badge rule", settled 2026-08-07): the header count is
// always the number of leaf-level rendered DATA points - table rows, list
// items, nested-list leaves. Prose/interpretation blocks (paragraph) and
// meta commentary (status, disclosure) are rendered but never counted,
// regardless of response type. A trailing `paragraph` fell through the old
// reduce's `: 1` default and inflated a 10-value card to "12 items".
const isUncounted = (s: StructuredSection) => isStatus(s) || isParagraph(s) || isDisclosure(s);

// #44 (rule 16): the S2 header count is the number of LEAF-level content
// lines. A nested_list's leaves are its children + group items (not its
// top-level entry count), so counting `items.length` under-reported the
// header vs the bullets actually rendered. A bare entry (title only, no
// children/groups) is itself one leaf line.
const nestedListLeafCount = (items: unknown[]): number =>
  items.reduce<number>((n, entry) => {
    if (!entry || typeof entry !== 'object') return n + 1;
    const e = entry as { children?: unknown; groups?: unknown };
    let leaves = Array.isArray(e.children) ? e.children.length : 0;
    if (Array.isArray(e.groups)) {
      for (const g of e.groups) {
        const gi = (g as { items?: unknown })?.items;
        leaves += Array.isArray(gi) ? gi.length : 0;
      }
    }
    return n + (leaves > 0 ? leaves : 1);
  }, 0);

// #34/#35: the DailyMed panel's leaves are its rendered data points — one per
// interaction item plus one per severe-effect line (rule 16 count-badge rule:
// leaf-level rendered values only; headers, empty-state lines and the corpus
// footer are never counted).
const dailyMedLeafCount = (data: DailyMedPanelData): number =>
  (data.sections ?? []).reduce((n, sec) => n + (sec.items?.length ?? 0), 0) +
  (data.severe_effects ?? []).reduce((n, d) => n + (d.effects?.length ?? 0), 0);

export function leafCountOf(renderable: StructuredSection[]): number {
  return renderable.reduce(
    (sum, s) =>
      isUncounted(s)
        ? sum
        : sum +
          (isTable(s)
            ? s.rows?.length ?? 0
            : isNestedList(s)
              ? nestedListLeafCount((s.items as unknown[]) ?? [])
              : isDailyMedPanel(s)
                ? dailyMedLeafCount(s.data as DailyMedPanelData)
                : isList(s) || isCards(s)
                  ? s.items?.length ?? 0
                  : 1),
    0,
  );
}

/** One rendered section. Shared by the card body and any nested group inside
 * it, so a nested group cannot drift into a second rendering of the same
 * content variants. */
/**
 * Level-2 bullet. USER ORDER 2026-08-10 (approved against a mockup): a
 * severity/direction glyph replaces the plain bullet where the payload
 * carries one. Every key is produced deterministically upstream from a
 * STORED value (organ band, SDOH screening state, computed trend direction);
 * this component only maps key -> pixels and falls back to the plain bullet
 * for anything it does not recognise, so an unknown key can never blank a
 * line out.
 */
function ChildGlyph({ glyph }: { glyph?: string }) {
  const dot = (className: string) => (
    <span className={`mt-[7px] h-2 w-2 rounded-full shrink-0 ${className}`} aria-hidden="true" />
  );
  const arrow = (mark: string, className: string) => (
    <span className={`mt-px w-3 shrink-0 text-center text-[12px] leading-[1.35] ${className}`} aria-hidden="true">
      {mark}
    </span>
  );
  switch (glyph) {
    case 'critical':
      return dot('bg-red-500');
    case 'impaired':
    case 'barrier':
      return dot('bg-amber-500');
    case 'healthy':
    case 'screened_negative':
      return dot('bg-emerald-500');
    case 'unknown':
      return dot('bg-slate-400/70');
    case 'increased':
      return arrow('↗', 'text-amber-500');
    case 'decreased':
      return arrow('↘', 'text-sky-400');
    case 'unchanged':
      return arrow('→', 'text-slate-400');
    default:
      return <span className="mt-[8px] h-1 w-1.5 rounded-sm bg-sky-400/60 shrink-0" aria-hidden="true" />;
  }
}

/**
 * Level-1 entry title. A group header ("Active conditions:") is bold whole;
 * a standalone labelled line ("Age: 72 years; advanced age contributes …")
 * bolds only its label so the sentence after it reads as prose — the shape
 * the user approved against the mockup on 2026-08-10. Presentation only: the
 * split is on the label colon the summary builder itself wrote, and the text
 * is rendered verbatim either way.
 */
/**
 * Bold the leading "Label:" of a summary line so a labelled fact reads
 * label-then-prose ("**Age:** 72 years; advanced age contributes …"), the
 * shape approved against the mockup on 2026-08-10. Presentational only: the
 * colon is the one the summary builder itself wrote, the text is otherwise
 * untouched, and a line already carrying markdown emphasis is left alone.
 */
function boldLeadingLabel(text: string): string {
  if (text.startsWith('**') || text.startsWith('#')) return text;
  const at = text.indexOf(': ');
  if (at <= 0 || at > 48) return text;
  return `**${text.slice(0, at + 1)}**${text.slice(at + 1)}`;
}

function NestedEntryTitle({ title, hasChildren }: { title: string; hasChildren: boolean }) {
  if (hasChildren) return <span className="font-semibold">{title}</span>;
  const at = title.indexOf(': ');
  if (at <= 0) return <span className="font-semibold">{title}</span>;
  return (
    <>
      <span className="font-semibold">{title.slice(0, at + 1)}</span>
      {title.slice(at + 1)}
    </>
  );
}

function renderSection(section: StructuredSection, i: number) {
  if (isTable(section)) {
    // #122: a grouped payload (e.g. observations split into
    // Laboratory / Vital signs / Survey / Social history) emits one
    // table section per category WITH a title — render it as the
    // rule-16 ALL-CAPS subsection heading so sibling tables are
    // identifiable. Same label anatomy as list sections below.
    return (
      <div key={i} className="space-y-1.5">
        {section.title && (
          <div className="text-[11px] font-medium uppercase tracking-wide text-brand-text">
            {section.title}
          </div>
        )}
        <DataTable
          columns={section.columns as string[]}
          rows={section.rows as Record<string, unknown>[]}
          compact={(section.rows?.length ?? 0) >= 8}
        />
      </div>
    );
  }
  if (isList(section)) {
    return (
      <div key={i} className="space-y-1.5">
        {section.title && (
          <div className="text-[11px] font-medium uppercase tracking-wide text-brand-text">
            {section.title}
          </div>
        )}
        <ul className="space-y-1.5">
          {(section.items as unknown[]).map((item, j) => (
            <li key={j} className="flex gap-2">
              {/* USER ORDER 2026-08-10: a flat list line can carry its own
                  severity dot (the organ block keeps ONE line per organ and
                  colours it from the stored band). The key is supplied by the
                  backend in `itemGlyphs`; absent it, the default bullet is
                  byte-identical to before. */}
              {section.itemGlyphs?.[j] ? (
                <ChildGlyph glyph={section.itemGlyphs[j]} />
              ) : (
                <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-brand/80 shrink-0" />
              )}
              <ClinicalMarkdownBody
                content={boldLeadingLabel(String(item ?? ''))}
                className="prose prose-sm dark:prose-invert max-w-none min-w-0 flex-1 [&_p]:m-0 [&_p]:leading-[1.65] text-[13px]"
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (isNestedList(section)) {
    return (
      <div key={i} className="space-y-1.5">
        {section.title && (
          <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
            {section.title}
          </div>
        )}
        <ul className="space-y-2.5">
          {(section.items as NestedListItem[]).map((entry, j) => (
            <li key={j} className="space-y-1">
              {entry.title && (
                <div className="flex gap-2">
                  <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-sky-400/80 shrink-0" />
                  <span className="text-[13px] text-foreground min-w-0 flex-1">
                    <NestedEntryTitle title={entry.title} hasChildren={(entry.children?.length ?? 0) > 0} />
                    {/* USER ORDER 2026-08-10: every enumerated sub-section
                        shows how many items it holds. The backend sets this to
                        len() of the children rendered below, so the number can
                        never disagree with what is on screen. */}
                    {typeof entry.count === 'number' && entry.count > 0 && (
                      <span className="ml-1.5 text-[12px] text-muted-foreground">({entry.count})</span>
                    )}
                  </span>
                </div>
              )}
              {Array.isArray(entry.children) && entry.children.length > 0 && (
                <ul
                  className={
                    entry.columns === 2
                      ? 'grid grid-cols-1 gap-x-5 gap-y-1 pl-5 sm:grid-cols-2'
                      : 'space-y-1 pl-5'
                  }
                  data-testid={entry.columns === 2 ? 'nested-children-2col' : undefined}
                >
                  {entry.children.map((child, k) => (
                    <li key={k} className="flex gap-2 min-w-0">
                      <ChildGlyph glyph={entry.childGlyphs?.[k]} />
                      <ClinicalMarkdownBody
                        content={child}
                        className="prose prose-sm dark:prose-invert max-w-none min-w-0 flex-1 [&_p]:m-0 [&_p]:leading-[1.6] text-[12.5px]"
                      />
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(entry.groups) && entry.groups.length > 0 && (
                <ul className="space-y-1.5 pl-5">
                  {entry.groups.map((group, gk) => (
                    <li key={gk} className="space-y-1">
                      <div className="flex gap-2">
                        <span className="mt-[8px] h-1 w-1.5 rounded-sm bg-sky-400/60 shrink-0" />
                        <span className="text-[12.5px] font-semibold text-foreground/90 min-w-0 flex-1">
                          {group.label}
                        </span>
                      </div>
                      {Array.isArray(group.items) && group.items.length > 0 && (
                        <ul className="space-y-1 pl-5">
                          {group.items.map((gitem, gi) => (
                            <li key={gi} className="flex gap-2">
                              <span className="mt-[8px] h-1 w-1 rounded-full bg-sky-300/60 shrink-0" />
                              <ClinicalMarkdownBody
                                content={gitem}
                                className="prose prose-sm dark:prose-invert max-w-none min-w-0 flex-1 [&_p]:m-0 [&_p]:leading-[1.6] text-[12.5px]"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (isStatus(section)) {
    return (
      <div key={i} className="text-[12px] italic text-muted-foreground/80 border-t border-sky-200/30 dark:border-sky-800/25 pt-2.5">
        {section.content}
      </div>
    );
  }
  if (isDisclosure(section)) {
    return <SystemDisclosure key={i} content={String(section.content ?? '')} />;
  }
  if (isCards(section)) {
    // Delegated rather than re-implemented: ResponseSection already
    // owns the accordion shape these payloads expect, and a second
    // implementation would drift from it.
    return <ResponseSection key={i} section={section} index={i} />;
  }
  if (isDailyMedPanel(section)) {
    // #34/#35: the DailyMed FDA-label panel replacing the standard CMA
    // sections (temporary, USER ORDER 2026-08-18).
    return <DailyMedSafetyPanel key={i} data={section.data as DailyMedPanelData} />;
  }
  return (
    <div key={i} className="space-y-1.5">
      {section.title && (
        <div className="text-[11px] font-medium uppercase tracking-wide text-brand-text">
          {section.title}
        </div>
      )}
      <ClinicalMarkdownBody
        // #58 req 6: any surviving "=== Section N: Title ==="
        // scaffolding becomes a plain header here, so summaries
        // already stored in the old format display correctly
        // without being regenerated. A no-op on text with no
        // delimiters, which is why it runs on every paragraph
        // rather than only on ones we guess came from the summary.
        content={stripSectionScaffolding(section.content)}
        className="prose prose-sm dark:prose-invert max-w-none [&_p]:leading-[1.65] text-[13px]"
      />
    </div>
  );
}

/**
 * Collapsible panel for the 3-part response layout. Renders through the
 * canonical S2Card shell (ticket #101/WS-5) - this file now owns only the
 * content-variant rendering (table/list/nested_list/paragraph/status), while
 * S2Card owns the bordered-card/header/chevron/collapse anatomy shared with
 * every other Section-2 content family.
 *
 * Gated solely on structured.meta.adk_clinical_facts_panel === true. Used by
 * the chart-data 3-part pilot (table sections, "Clinical Facts") and the CMA
 * report (list/paragraph sections, "Facts & Reasoning" via the title prop).
 */
export function ClinicalFactsPanel({
  sections,
  title = 'Clinical Facts',
  nestedGroup,
  headerAction,
  codeReferences,
}: {
  sections: StructuredSection[];
  title?: string;
  /** Card-level action rendered in this panel's own header row — today the
   * Trend Analysis card's link to the chart page. It belongs to the card whose
   * content it acts on, so it never travels to the nested summary card. */
  headerAction?: ReactNode;
  /** #27: content that renders as a collapsible group INSIDE this one card,
   * above the card's own sections — currently the prepared Patient Summary.
   * It used to mount as a second S2Card sibling, which rule 16 forbids. Its
   * leaves count toward THIS card's badge, because rule 16 defines that badge
   * as the leaf-level lines across all subsections of the one Section-2 card. */
  nestedGroup?: { title: string; sections: StructuredSection[] };
  /** DEPRECATED by USER ORDER #116 (2026-08-07) and no longer rendered here.
   * References are now their own sibling collapsible below this card, mounted
   * by FinalResponseRenderer, and only on the CMA and Knowledge-RAG routes.
   * Kept on the signature so existing callers compile unchanged; passing it
   * has no effect. */
  codeReferences?: CodeRef[];
}) {
  const renderable = renderableSectionsOf(sections);
  const nestedRenderable = renderableSectionsOf(nestedGroup?.sections ?? []);
  if (!renderable.length && !nestedRenderable.length) return null;

  // Preserve the chart-data pilot's exact "N rows" wording; mixed/list content
  // counts as items instead. Each card's unit follows its OWN sections.
  const unitOf = (secs: StructuredSection[]) =>
    secs.some((s) => !isTable(s) && !isUncounted(s)) ? 'item' : 'row';

  /*
    #50 SECOND CORRECTION (USER ORDER 2026-08-10, live screenshot). Verbatim:
    "Why do we still have the 'Patient Summary' inside the 'Review &
    Recommendations' collapsible?! And why there is another 'Review &
    Recommendations' inside the 'Review & Recommendations' collapsible?"

    My first #50 fix kept ONE outer S2Card (titled "Review & Recommendations"
    per the 2026-08-06 rename) and put both groups inside it — which still
    reads as the summary nested inside an R&R collapsible, plus a duplicated
    R&R label. "Peer collapsible above" means NO wrapper: when the prepared
    Patient Summary exists, Section 2 renders TWO sibling S2Cards — the
    summary card first, the review card (this panel's `title` prop) below it,
    each with its own count badge. No group is ever titled with its parent's
    name. The chart-data pilot (no summary group) keeps its single card.

    #108 (collapsibles arrive CLOSED) carries over via S2Card's default.
  */
  const summaryCard =
    nestedRenderable.length > 0 && nestedGroup ? (
      <S2Card
        title={nestedGroup.title}
        count={leafCountOf(nestedRenderable)}
        unit={unitOf(nestedRenderable)}
      >
        {nestedRenderable.map((section, i) => renderSection(section, i))}
      </S2Card>
    ) : null;

  const reviewCard =
    renderable.length > 0 ? (
      <S2Card
        title={title}
        count={leafCountOf(renderable)}
        unit={unitOf(renderable)}
        headerAction={headerAction}
      >
        {renderable.map((section, i) => renderSection(section, i))}
        {/*
          USER ORDER #116 (2026-08-07) REVERSES #83. Verbatim: "In fact, the
          'References' is not inside 'Clinical Facts' collapsible. It is a
          separate collapsible below 'Clinical Facts'."

          #83 had nested it here, below a divider inside this card. It is now
          mounted by FinalResponseRenderer as its own sibling collapsible AFTER
          this one, so this panel renders nothing for it. The prop is kept on
          the signature (and deliberately unused) so every existing caller
          keeps compiling while the renderer owns placement.
        */}
      </S2Card>
    ) : null;

  return (
    <>
      {summaryCard}
      {reviewCard}
    </>
  );
}
